import { AlertCircle, Home, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui';

interface ErrorPageProps {
  /** Error title */
  title: string;
  /** Error message */
  message: string;
  /** Optional error code to display */
  code?: string;
  /** Retry handler */
  onRetry?: () => void;
  /** Show link to home/dashboard */
  showHomeLink?: boolean;
  /**
   * Use native anchor tag instead of React Router Link.
   * Required when rendered outside of Router context (e.g., ErrorBoundary fallback).
   */
  useNativeLink?: boolean;
}

/**
 * User-friendly error page displayed when:
 * - React component crashes (via ErrorBoundary)
 * - API returns 500 error
 * - Network is unavailable
 *
 * @see Spec 0012 Section 7.4 - ErrorPage Component
 */
export function ErrorPage({
  title,
  message,
  code,
  onRetry,
  showHomeLink = true,
  useNativeLink = false,
}: ErrorPageProps) {
  const { t } = useTranslation('errors');

  // Home link - use native anchor when outside Router context (ErrorBoundary fallback)
  const homeLink = showHomeLink && (
    useNativeLink ? (
      <a href="/">
        <Button variant={onRetry ? 'outline' : 'default'}>
          <Home className="mr-2 h-4 w-4" />
          {t('page.goHome')}
        </Button>
      </a>
    ) : (
      <a href="/">
        <Button variant={onRetry ? 'outline' : 'default'}>
          <Home className="mr-2 h-4 w-4" />
          {t('page.goHome')}
        </Button>
      </a>
    )
  );

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-8 w-8 text-destructive" aria-hidden="true" />
          </div>
          <CardTitle className="text-xl">{title}</CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          <p className="text-muted-foreground">{message}</p>
          {code && (
            <p className="mt-2 font-mono text-sm text-muted-foreground">{t('api.errorCode', { code })}</p>
          )}
        </CardContent>
        <CardFooter className="flex justify-center gap-4">
          {onRetry && (
            <Button onClick={onRetry} variant="default">
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('common:button.retry')}
            </Button>
          )}
          {homeLink}
        </CardFooter>
      </Card>
    </div>
  );
}
